/**
 * cycle-5c+25-fix25 (Owner-direct 2026-05-26): cross-reference autolinking.
 *
 * Owner-direct chat ([C16] example): *"if it says 'read the 7-question
 * workbook' then link to it.. OR... at least list the card number so i
 * can quickly look it up"*.
 *
 * Two layers:
 *
 *   1. `<XrefText text="..." />` — takes a string of canon-derived text
 *      (row notes, gate text, decision summaries, etc.) and renders it
 *      with [Cn] tokens + MP_INBOX/<file>.md references auto-linked. The
 *      links navigate to the corresponding card via `?item=<type>:<id>`.
 *
 *   2. `findRelatedItems(item, allItems)` — discovers cards that share
 *      a slug stem with the given item (e.g. the `RI-onboarding-phase-A`
 *      promise + the `2026-05-21_mp-to-owner_RI-onboarding-workbook-PLAIN`
 *      workbook share the `RI-onboarding` stem and are surfaced as related
 *      even though neither row mentions the other's [Cn] explicitly).
 *
 * Both layers respect the React Router pattern used elsewhere in the app:
 * navigation is via `navigate(`/?item=${type}:${original_id}`)` so the
 * existing item-selection logic in RoadmapPage handles the rest.
 */

import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useXrefLookup } from "./XrefContext";

const REF_RE = /\[(C\d+)\]/g;
const WORKBOOK_PATH_RE = /(?:MP_INBOX\/)?(\d{4}-\d{2}-\d{2}_[\w-]+(?:-workbook-PLAIN|-workbook|-arbitration)?)\.md/g;

/**
 * Tokenise `text` into an alternating sequence of {kind:'text'|'ref'|'workbook', value}.
 * Used by <XrefText> to render links inline. Order is preserved.
 */
export function tokenizeXref(text) {
  if (!text || typeof text !== "string") return [{ kind: "text", value: String(text || "") }];

  // Two-pass scan: collect all match ranges from both regexes, sort by start
  // index, render text between matches as plain. This avoids the
  // single-regex limitation of having to choose one pattern at a time.
  const matches = [];
  REF_RE.lastIndex = 0;
  let m;
  while ((m = REF_RE.exec(text)) !== null) {
    matches.push({
      kind: "ref",
      start: m.index,
      end: m.index + m[0].length,
      ref: m[1],
      raw: m[0],
    });
  }
  WORKBOOK_PATH_RE.lastIndex = 0;
  while ((m = WORKBOOK_PATH_RE.exec(text)) !== null) {
    matches.push({
      kind: "workbook",
      start: m.index,
      end: m.index + m[0].length,
      workbook_id: m[1],
      raw: m[0],
    });
  }
  matches.sort((a, b) => a.start - b.start);

  // Drop any match that overlaps a prior match's range (refs win because
  // they're more specific, and we processed refs first so they appear first
  // in sort order on identical start).
  const filtered = [];
  let cursor = -1;
  for (const mm of matches) {
    if (mm.start < cursor) continue;
    filtered.push(mm);
    cursor = mm.end;
  }

  const out = [];
  let pos = 0;
  let tokenSeq = 0;
  for (const mm of filtered) {
    if (mm.start > pos) {
      out.push({ kind: "text", value: text.slice(pos, mm.start), _key: `text-${tokenSeq++}-${mm.start}` });
    }
    out.push({ ...mm, _key: `${mm.kind}-${mm.start}-${mm.ref || mm.workbook_id || ""}` });
    pos = mm.end;
    tokenSeq++;
  }
  if (pos < text.length) {
    out.push({ kind: "text", value: text.slice(pos), _key: `text-${tokenSeq++}-tail` });
  }
  return out;
}

/**
 * <XrefText text="..." /> — renders text with [Cn] + MP_INBOX/<file>.md
 * references replaced by clickable buttons that navigate to the matching
 * card.  Falls back to plain text rendering for tokens that don't resolve
 * against the current xref lookup (avoids broken links for refs that don't
 * exist in the current canon snapshot — e.g. a row mentioning [C99] when
 * we only have C1–C76).
 */
export function XrefText({ text, className, style }) {
  const lookup = useXrefLookup();
  const navigate = useNavigate();
  const tokens = useMemo(() => tokenizeXref(text), [text]);
  const onNav = useCallback(
    (type, id) => navigate(`/?item=${encodeURIComponent(`${type}:${id}`)}`),
    [navigate],
  );

  return (
    <span className={className} style={style}>
      {tokens.map((t, i) => {
        if (t.kind === "text") {
          return <span key={t._key}>{t.value}</span>;
        }
        if (t.kind === "ref") {
          const hit = lookup?.byRef?.[t.ref];
          if (!hit) {
            return <span key={t._key}>{t.raw}</span>;
          }
          return (
            <button
              key={t._key}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNav(hit.type, hit.original_id);
              }}
              className="text-mono inline-flex items-center transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              style={{
                fontSize: "inherit",
                fontWeight: 700,
                color: "hsl(var(--primary))",
                background: "hsl(var(--primary) / 0.10)",
                padding: "0 0.25rem",
                borderRadius: "0.2rem",
              }}
              data-testid={`xref-ref-${t.ref}`}
              title={`Jump to [${t.ref}] ${hit.title || hit.original_id}`}
            >
              [{t.ref}]
            </button>
          );
        }
        if (t.kind === "workbook") {
          const hit = lookup?.byWorkbookId?.[t.workbook_id];
          if (!hit) {
            // Workbook isn't in current dashboard view — still link by best-guess to ?item=workbook:<id>
            return (
              <button
                key={t._key}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNav("workbook", t.workbook_id);
                }}
                className="text-mono inline-flex transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                style={{
                  fontSize: "inherit",
                  fontWeight: 500,
                  color: "hsl(var(--primary))",
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                }}
                data-testid={`xref-workbook-${t.workbook_id}`}
                title={`Jump to workbook ${t.workbook_id}`}
              >
                {t.raw}
              </button>
            );
          }
          return (
            <button
              key={t._key}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNav("workbook", t.workbook_id);
              }}
              className="text-mono inline-flex transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              style={{
                fontSize: "inherit",
                fontWeight: 500,
                color: "hsl(var(--primary))",
              }}
              data-testid={`xref-workbook-${t.workbook_id}`}
              title={`Jump to workbook ${hit.title || t.workbook_id}`}
            >
              {hit.ref ? `[${hit.ref}] ` : ""}
              {t.raw}
            </button>
          );
        }
        return <span key={t._key || `unknown-${i}`}>{t.raw || ""}</span>;
      })}
    </span>
  );
}

/**
 * Find related cards by shared slug stem.
 *
 * Two-pass:
 *   1. Extract stem candidates from the current item's row_id by splitting
 *      on hyphens/underscores and grouping consecutive non-trivial words
 *      (length > 2, not common stopwords like 'phase', 'workbook', etc.).
 *   2. Match other items whose row_id contains AT LEAST ONE of those stems
 *      (case-sensitive — preserves slug-formatted casing like "RI-onboarding").
 *
 * Excludes the current item itself + any item already visible via an
 * explicit [Cn] reference in the current item's notes/gate (since those
 * will be linked inline by <XrefText>).
 */
const STOP_TOKENS = new Set([
  "phase", "phase-a", "phase-b", "phase-c", "phase-d",
  "workbook", "plain", "mp-to-owner", "owner-to-mp",
  "to", "and", "or", "the", "for", "with", "via",
  "rec", "init", "initial", "v1", "v2", "v3", "iter",
]);

function extractStems(rowId) {
  if (!rowId || typeof rowId !== "string") return [];
  const parts = rowId.split(/[-_]/).filter((p) => {
    if (!p) return false;
    if (p.length <= 2) return false;
    if (STOP_TOKENS.has(p.toLowerCase())) return false;
    if (/^\d+$/.test(p)) return false;  // pure number
    if (/^\d{4}-\d{2}-\d{2}$/.test(p)) return false;  // date
    return true;
  });
  // Take the first 3 "important" parts as the stem set — beyond 3, the slug
  // is usually overly specific and would match too many unrelated items.
  return parts.slice(0, 3);
}

export function findRelatedItems(currentItem, allItems, maxResults = 5) {
  if (!currentItem || !Array.isArray(allItems)) return [];
  const originalId = currentItem.id?.includes(":")
    ? currentItem.id.substring(currentItem.id.indexOf(":") + 1)
    : currentItem.id;
  const stems = extractStems(originalId);
  if (stems.length === 0) return [];

  // Already-referenced refs (don't surface as "related" if already linked
  // inline via XrefText elsewhere in the row text).
  const alreadyLinked = new Set();
  REF_RE.lastIndex = 0;
  const haystack = `${currentItem.title || ""} ${currentItem.notes_summary || ""} ${currentItem.gate || ""}`;
  let mm;
  while ((mm = REF_RE.exec(haystack)) !== null) {
    alreadyLinked.add(mm[1]);
  }

  const scored = [];
  for (const it of allItems) {
    if (!it || it.id === currentItem.id) continue;
    if (it.ref && alreadyLinked.has(it.ref)) continue;
    const itOriginalId = it.id?.includes(":")
      ? it.id.substring(it.id.indexOf(":") + 1)
      : it.id;
    let score = 0;
    for (const stem of stems) {
      if (itOriginalId && itOriginalId.includes(stem)) score += 2;
      if (it.title && it.title.includes(stem)) score += 1;
    }
    if (score >= 2) {
      scored.push({ item: it, score });
    }
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, maxResults).map((s) => s.item);
}

/**
 * Build the xref lookup map from a unified-items array.
 *
 *   {
 *     byRef:        { "C11": {type, original_id, title}, ... },
 *     byWorkbookId: { "2026-05-21_...-workbook-PLAIN": {ref, title}, ... },
 *   }
 *
 * Consumed by the <XrefProvider> in RoadmapPage and read by <XrefText> + any
 * other component that wants to surface cross-reference badges.
 */
export function buildXrefLookup(items) {
  const byRef = {};
  const byWorkbookId = {};
  if (!Array.isArray(items)) return { byRef, byWorkbookId };
  for (const it of items) {
    const originalId = it.id?.includes(":")
      ? it.id.substring(it.id.indexOf(":") + 1)
      : it.id;
    if (it.ref) {
      byRef[it.ref.toUpperCase()] = {
        type: it.type,
        original_id: originalId,
        title: it.title,
      };
    }
    if (it.type === "workbook") {
      byWorkbookId[originalId] = {
        ref: it.ref,
        title: it.title,
      };
    }
  }
  return { byRef, byWorkbookId };
}
